import type { KeyboardEvent as ReactKeyboardEvent } from 'react';
import { PlatformGlyph } from '@/features/manager-breaks-demo/components/PlatformGlyph';
import {
  PLATFORM_QUOTE_BORDER,
  PLATFORM_TEXT,
  SEVERITY_BAR,
  SEVERITY_DOT,
} from '@/features/manager-breaks-demo/components/break-styles';
import type { BreakSeverity } from '@/features/manager-breaks-demo/types/breaks';
import type { PatternCase } from '../types/patterns';
import { OwnerAvatar } from './OwnerAvatar';

/** Resting border + ring + shadow so a critical case reads as hot at a glance.
 *  Border color tracks severity so it never clashes with the colored ring. */
const CARD_EMPHASIS: Record<BreakSeverity, string> = {
  high: 'border-rose-200 shadow-[0_20px_44px_-26px_rgba(244,63,94,0.5)] hover:shadow-[0_26px_54px_-24px_rgba(244,63,94,0.55)]',
  medium:
    'border-amber-100 shadow-[0_18px_40px_-28px_rgba(0,0,0,0.22)] hover:shadow-[0_24px_50px_-26px_rgba(0,0,0,0.28)]',
  low: 'border-zinc-100 shadow-[0_18px_40px_-28px_rgba(0,0,0,0.25)] hover:shadow-[0_24px_50px_-26px_rgba(0,0,0,0.3)]',
};

interface PatternCaseCardProps {
  patternCase: PatternCase;
  /** Opens the full examine view for this case's underlying break. */
  onDeepDive: (breakId: string) => void;
}

/**
 * One concrete violation, shown as a self-contained detail card: reference +
 * age, a two-line title, the conflicting evidence quote, the platforms it spans,
 * the owner, and the case's own impact + action. Legible at a glance; click
 * anywhere (except the quick action) to deep dive into the full examine view.
 */
export function PatternCaseCard({
  patternCase,
  onDeepDive,
}: PatternCaseCardProps) {
  const quoteBorder = PLATFORM_QUOTE_BORDER[patternCase.platforms[0] ?? 'jira'];
  const deepDiveBreakId = patternCase.breakId;
  const openDeepDive = deepDiveBreakId
    ? () => onDeepDive(deepDiveBreakId)
    : undefined;

  const interactiveProps = openDeepDive
    ? {
        role: 'button',
        tabIndex: 0,
        onClick: openDeepDive,
        onKeyDown: (event: ReactKeyboardEvent) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            openDeepDive();
          }
        },
        'aria-label': `Deep dive into ${patternCase.ref}`,
      }
    : {};

  return (
    <div
      {...interactiveProps}
      className={`group relative flex flex-col overflow-hidden rounded-2xl border bg-white p-5 text-start outline-none transition-all duration-300 focus-visible:ring-2 focus-visible:ring-[#00AEEF]/50 ${
        openDeepDive ? 'cursor-pointer hover:-translate-y-1' : ''
      } ${CARD_EMPHASIS[patternCase.severity]}`}
    >
      <span
        className={`pointer-events-none absolute inset-x-0 top-0 h-1 ${
          SEVERITY_BAR[patternCase.severity]
        }`}
      />

      {/* Reference + age */}
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span
            className={`h-2 w-2 rounded-full ${
              SEVERITY_DOT[patternCase.severity]
            }`}
          />
          <span className="font-mono text-xs font-medium tracking-tight text-zinc-700">
            {patternCase.ref}
          </span>
        </div>
        <span className="text-[11px] font-medium text-zinc-400">
          {patternCase.ageLabel}
        </span>
      </div>

      {/* Title */}
      <h4 className="mb-3 text-sm font-medium leading-snug text-black transition-colors group-hover:text-[#0052CC]">
        {patternCase.title}
      </h4>

      {/* Conflicting evidence */}
      <blockquote
        className={`mb-4 border-s-2 bg-rose-50/40 py-1.5 pe-2 ps-3 text-xs leading-relaxed text-zinc-600 ${quoteBorder}`}
      >
        <span className="text-zinc-700">“{patternCase.quote}”</span>
        <span className="mt-0.5 block text-[11px] text-zinc-400">
          — {patternCase.attribution}
        </span>
      </blockquote>

      {/* Platforms + owner */}
      <div className="mt-auto flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          {patternCase.platforms.map((platform) => (
            <span
              key={platform}
              className="flex h-7 w-7 items-center justify-center rounded-full border border-zinc-100 bg-white shadow-sm"
            >
              <PlatformGlyph
                platform={platform}
                className={`h-3.5 w-3.5 ${PLATFORM_TEXT[platform]}`}
              />
            </span>
          ))}
        </div>
        <OwnerAvatar owner={patternCase.owner} />
      </div>
    </div>
  );
}
